/**
 * Valores de partida.
 *
 * Nenhum número aqui é verdade sobre a Gráfica Boa União — são pontos de
 * partida documentados, para o sistema funcionar no primeiro dia sem esperar
 * uma reunião. Todos aparecem na tela de parâmetros e a gráfica ajusta.
 *
 * A regra vale para o módulo inteiro: constante no código é valor de partida;
 * o que varia entre gráficas vive em parâmetro (PRD RN-061).
 */

import type { CategoriaMotivo, Parametros } from './tipos.js';

export const PARAMETROS_DE_PARTIDA: Parametros = {
  toleranciaQuantidadePct: 10,
  alertaEtapaPct: 150,
  alertaEtapaParadaHoras: 4,
  inatividadeTabletMin: 5,
  desvioRelogioAvisoSeg: 300,
  acabamentoEsperaImpressoes: true,
  operadorEmVariasEtapas: false,
  operadorVeTodosSetores: true,
  atrasoContaAte: 'pronta',
  diasNoConjuntoDeTrabalho: 7,
};

export interface DescricaoParametro {
  chave: keyof Parametros;
  titulo: string;
  ajuda: string;
  tipo: 'numero' | 'booleano' | 'opcao';
  opcoes?: Array<{ valor: string; rotulo: string }>;
  unidade?: string;
  min?: number;
  max?: number;
}

/** O que a tela de parâmetros mostra, na ordem em que faz sentido ler. */
export const DESCRICAO_PARAMETROS: DescricaoParametro[] = [
  {
    chave: 'toleranciaQuantidadePct',
    titulo: 'Tolerância de quantidade',
    ajuda:
      'Quanto a quantidade apontada pode passar da prevista antes de a etapa ser marcada para revisão. Passar do limite não recusa o apontamento.',
    tipo: 'numero',
    unidade: '%',
    min: 0,
    max: 100,
  },
  {
    chave: 'alertaEtapaPct',
    titulo: 'Alerta de etapa demorada',
    ajuda: 'Percentual do tempo previsto a partir do qual a etapa em execução vira alerta.',
    tipo: 'numero',
    unidade: '%',
    min: 100,
    max: 500,
  },
  {
    chave: 'alertaEtapaParadaHoras',
    titulo: 'Alerta de etapa parada',
    ajuda: 'Horas úteis que uma etapa pode ficar na fila, com a estação livre, antes de alertar.',
    tipo: 'numero',
    unidade: 'h',
    min: 1,
    max: 72,
  },
  {
    chave: 'acabamentoEsperaImpressoes',
    titulo: 'Acabamento espera as impressões',
    ajuda:
      'Quando ligado, uma etapa de acabamento avisa se ainda há impressão pendente na OS. É aviso, não bloqueio.',
    tipo: 'booleano',
  },
  {
    chave: 'operadorEmVariasEtapas',
    titulo: 'Operador em várias etapas',
    ajuda: 'Permite que a mesma pessoa fique com mais de uma etapa em execução ao mesmo tempo.',
    tipo: 'booleano',
  },
  {
    chave: 'operadorVeTodosSetores',
    titulo: 'Operador vê todos os setores',
    ajuda: 'Ver não é apontar: apontar continua restrito aos setores liberados para a pessoa.',
    tipo: 'booleano',
  },
  {
    chave: 'atrasoContaAte',
    titulo: 'A OS deixa de atrasar quando',
    ajuda:
      'Se o relógio do prazo para quando a produção termina, ou só quando o material sai da gráfica.',
    tipo: 'opcao',
    opcoes: [
      { valor: 'pronta', rotulo: 'Fica pronta' },
      { valor: 'concluida', rotulo: 'É entregue ou retirada' },
    ],
  },
  {
    chave: 'inatividadeTabletMin',
    titulo: 'Bloqueio do tablet',
    ajuda: 'Minutos sem toque até o tablet pedir o PIN de novo.',
    tipo: 'numero',
    unidade: 'min',
    min: 1,
    max: 120,
  },
  {
    chave: 'desvioRelogioAvisoSeg',
    titulo: 'Desvio de relógio tolerado',
    ajuda:
      'Diferença entre o relógio do tablet e o do servidor que dispara aviso. O apontamento nunca é invalidado por isso.',
    tipo: 'numero',
    unidade: 's',
    min: 30,
    max: 3600,
  },
  {
    chave: 'diasNoConjuntoDeTrabalho',
    titulo: 'Histórico no tablet',
    ajuda: 'Dias que uma OS concluída continua baixada no aparelho antes de sair do conjunto.',
    tipo: 'numero',
    unidade: 'dias',
    min: 1,
    max: 90,
  },
];

/** Motivos de pausa de partida (PRD §17.1 J-10 — a confirmar com a gráfica). */
export const MOTIVOS_DE_PARTIDA: Array<{
  codigo: string;
  descricao: string;
  categoria: CategoriaMotivo;
  exigeTexto: boolean;
}> = [
  { codigo: 'ACERTO', descricao: 'Acerto de máquina / troca de chapa', categoria: 'maquina', exigeTexto: false },
  { codigo: 'MANUT', descricao: 'Manutenção ou quebra', categoria: 'maquina', exigeTexto: false },
  { codigo: 'MATERIAL', descricao: 'Falta de material', categoria: 'material', exigeTexto: false },
  { codigo: 'ARTE', descricao: 'Problema na arte', categoria: 'arte', exigeTexto: false },
  { codigo: 'ESPERA', descricao: 'Aguardando etapa anterior', categoria: 'espera', exigeTexto: false },
  { codigo: 'ALMOCO', descricao: 'Almoço / intervalo', categoria: 'pessoal', exigeTexto: false },
  { codigo: 'FIMTURNO', descricao: 'Fim de turno', categoria: 'pessoal', exigeTexto: false },
  { codigo: 'OUTRO', descricao: 'Outro motivo', categoria: 'outro', exigeTexto: true },
];
